import React from "react";
import { TenantSelectRow } from "../TenantSelectRow";
import {
  List,
  StatusText,
  ErrorText,
  RetryButton,
  SkeletonRow,
} from "./TenantSelectList.styles";
import type { TenantSelectListProps } from "./TenantSelectList.types";

const SKELETON_COUNT = 3;

/**
 * Scrollable, single-select list of tenants for the login selection step.
 *
 * Stateless: the chosen id comes in as `selectedTenantId` and every click goes
 * straight back out through `onSelect`, unchanged. The toggle logic (clicking
 * the selected row unchecks it; clicking another replaces it) lives in
 * `useTenantSelectInternalState`, not here.
 *
 * `role="radiogroup"` pairs with each row's `role="radio"` so assistive tech
 * announces "1 of N" rather than N independent toggles.
 */
export const TenantSelectList: React.FC<TenantSelectListProps> = ({
  tenants,
  selectedTenantId,
  onSelect,
  isLoading = false,
  errorMessage = null,
  onRetry,
  disabled = false,
  className,
}) => {
  // Error wins over everything: a stale list next to a failure message would
  // imply the retry already succeeded.
  if (errorMessage) {
    return (
      <div className={className}>
        <ErrorText role="alert">{errorMessage}</ErrorText>
        {onRetry && (
          <RetryButton type="button" onClick={onRetry}>
            Try again
          </RetryButton>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <List className={className} aria-busy="true" aria-live="polite">
        {Array.from({ length: SKELETON_COUNT }, (_, i) => (
          <SkeletonRow key={i} data-testid="tenant-skeleton-row" />
        ))}
      </List>
    );
  }

  if (tenants.length === 0) {
    return (
      <StatusText className={className}>
        No organizations are available for this account.
      </StatusText>
    );
  }

  return (
    <List className={className} role="radiogroup" aria-label="Select an organization">
      {tenants.map((tenant) => (
        <TenantSelectRow
          key={tenant.tenant_id}
          tenant={tenant}
          selected={tenant.tenant_id === selectedTenantId}
          onSelect={onSelect}
          disabled={disabled}
        />
      ))}
    </List>
  );
};

export default TenantSelectList;
